import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Upload,
  message,
  Popconfirm,
  Image,
  Space,
  Tag,
  Select,
  Switch
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import request from '../utils/request';
import AuthService from '../utils/auth';

const { TextArea } = Input;
const { Option } = Select;

const SubcategoryList = () => {
  const [subcategories, setSubcategories] = useState([]);
  const [parentCategories, setParentCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [form] = Form.useForm();
  const [uploading, setUploading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [searchName, setSearchName] = useState('');
  const [searchParentId, setSearchParentId] = useState('');

  // 获取父分类选项（从一级分类中获取）
  const fetchParentCategories = async () => {
    try {
      const response = await request.get('/admin/category/list', {
        params: { page: 1, pageSize: 1000 }
      });
      if (response.success) {
        const categoryData = Array.isArray(response.data) ? response.data : response.data.list || [];
        setParentCategories(categoryData);
      }
    } catch (error) {
      console.error('获取父分类选项失败:', error);
    }
  };

  // 获取二级分类列表
  const fetchSubcategories = async (currentPage = page, currentPageSize = pageSize) => {
    setLoading(true);
    try {
      const response = await request.get('/admin/subcategory/list', {
        params: {
          page: currentPage,
          pageSize: currentPageSize,
          name: searchName,
          parent_category_id: searchParentId
        }
      });
      if (response.success) {
        setSubcategories(response.data.list || []);
        setTotal(response.data.pagination?.total || 0);
      } else {
        message.error('获取二级分类列表失败');
      }
    } catch (error) {
      message.error('获取二级分类列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchParentCategories();
    fetchSubcategories();
  }, []);

  useEffect(() => {
    fetchSubcategories(page, pageSize);
  }, [page, pageSize]);

  // 上传图片
  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch('/api/admin/upload/image', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: formData,
      });

      const result = await response.json();
      if (result.success) {
        return result.data.url;
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      message.error('图片上传失败，请检查网络后重试');
      return null;
    }
  };

  // 处理表单提交
  const handleSubmit = async (values) => {
    setUploading(true);
    try {
      let imageUrl = values.imageUrl;

      // 如果有新上传的文件
      if (values.image && values.image.length > 0) {
        const imageFile = values.image[0];
        if (imageFile.originFileObj) {
          imageUrl = await uploadImage(imageFile.originFileObj);
          if (!imageUrl) {
            setUploading(false);
            return;
          }
        }
      }

      const submitData = {
        name: values.name,
        parent_category_id: values.parent_id,
        description: values.description || '',
        image_url: imageUrl,
        status: values.status ? 1 : 0
      };

      let response;
      if (editingSubcategory) {
        response = await request.put(`/admin/subcategory/${editingSubcategory.id}`, submitData);
      } else {
        response = await request.post('/admin/subcategory', submitData);
      }

      if (response.success) {
        message.success(editingSubcategory ? '更新成功' : '创建成功');
        setModalVisible(false);
        fetchSubcategories();
      } else {
        message.error(response.message || '操作失败');
      }
    } catch (error) {
      message.error('操作失败');
    } finally {
      setUploading(false);
    }
  };

  // 删除二级分类
  const handleDelete = async (id) => {
    try {
      const response = await request.delete(`/admin/subcategory/${id}`);
      if (response.success) {
        message.success('删除成功');
        fetchSubcategories();
      } else {
        message.error(response.message || '删除失败');
      }
    } catch (error) {
      message.error('删除失败');
    }
  };

  // 搜索
  const handleSearch = () => {
    setPage(1);
    fetchSubcategories(1, pageSize);
  };

  // 重置搜索
  const handleReset = () => {
    setSearchName('');
    setSearchParentId('');
    setPage(1);
    fetchSubcategories(1, pageSize);
  };

  // 打开添加/编辑模态框
  const openModal = (subcategory = null) => {
    setEditingSubcategory(subcategory);
    setModalVisible(true);
    if (subcategory) {
      form.setFieldsValue({
        ...subcategory,
        status: subcategory.status === 1
      });
    } else {
      form.resetFields();
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: '分类名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '父分类',
      dataIndex: 'parent_name',
      key: 'parent_name',
      render: (text) => <Tag color="blue">{text}</Tag>
    },
    {
      title: '分类图片',
      dataIndex: 'image',
      key: 'image',
      render: (text) => text ? (
        <Image
          width={50}
          height={50}
          src={text}
          style={{ objectFit: 'cover' }}
          fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
        />
      ) : '-',
      width: 80,
    },
    {
      title: '商品数量',
      dataIndex: 'product_count',
      key: 'product_count',
      width: 80,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 1 ? 'success' : 'default'}>
          {status === 1 ? '启用' : '禁用'}
        </Tag>
      ),
      width: 80,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => new Date(text).toLocaleDateString(),
      width: 120,
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => openModal(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个二级分类吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
      width: 120,
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card title="二级分类管理">
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Input
              placeholder="分类名称"
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              style={{ width: 200 }}
            />
            <Select
              placeholder="选择父分类"
              value={searchParentId}
              onChange={setSearchParentId}
              style={{ width: 200 }}
              allowClear
            >
              {parentCategories.map(cat => (
                <Option key={cat.id} value={cat.id}>{cat.name}</Option>
              ))}
            </Select>
            <Button type="primary" onClick={handleSearch}>
              搜索
            </Button>
            <Button onClick={handleReset}>
              重置
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => openModal()}
            >
              新增二级分类
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={subcategories}
          rowKey="id"
          loading={loading}
          pagination={{
            current: page,
            pageSize: pageSize,
            total: total,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`,
          }}
        />
      </Card>

      <Modal
        title={editingSubcategory ? '编辑二级分类' : '新增二级分类'}
        open={modalVisible}
        onOk={() => form.submit()}
        onCancel={() => setModalVisible(false)}
        confirmLoading={uploading}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label="分类名称"
            rules={[{ required: true, message: '请输入分类名称' }]}
          >
            <Input placeholder="请输入分类名称" />
          </Form.Item>

          <Form.Item
            name="parent_id"
            label="父分类"
            rules={[{ required: true, message: '请选择父分类' }]}
          >
            <Select placeholder="请选择父分类">
              {parentCategories.map(cat => (
                <Option key={cat.id} value={cat.id}>{cat.name}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label="分类描述"
          >
            <TextArea rows={3} placeholder="请输入分类描述（可选）" />
          </Form.Item>

          <Form.Item
            name="image"
            label="分类图片"
          >
            <Upload
              listType="picture-card"
              maxCount={1}
              beforeUpload={() => false}
            >
              <div>
                <PlusOutlined />
                <div style={{ marginTop: 8 }}>上传图片</div>
              </div>
            </Upload>
          </Form.Item>

          <Form.Item
            name="status"
            label="启用状态"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SubcategoryList;

